import { useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, Star } from "lucide-react";
import { ImageUpload } from "./ImageUpload";
import { TranslationsPanel, type I18nMap } from "./TranslationsPanel";
import type { SiteConfig, WholesaleConfig, WholesaleTier } from "./AdminDataContext";

interface AdminWholesaleTabProps {
  config: SiteConfig;
  setConfig: (c: SiteConfig) => void;
}

const emptyTier: WholesaleTier = { name: "", minOrder: "", priceRange: "", features: [""] };
const emptyFaq = { q: "", a: "" };

export function AdminWholesaleTab({ config, setConfig }: AdminWholesaleTabProps) {
  const [section, setSection] = useState<"hero" | "description" | "showcase" | "tiers" | "certifications" | "faqs" | "inquiry">("hero");

  const ws: WholesaleConfig = config.wholesale || {
    hero: { label: "", title: "", subtitle: "", image: "" },
    description: { label: "", title: "", text: "" },
    showcase: { label: "", title: "", features: [], image: "", cta_text: "" },
    tiers: [],
    faqs: [],
    inquiry: { label: "", title: "", subtitle: "" },
    certifications: [],
  };

  const update = (patch: Partial<WholesaleConfig>) => {
    setConfig({ ...config, wholesale: { ...ws, ...patch } });
  };

  const labelStyle = "block text-[#6F675F] tracking-[0.12em] uppercase mb-2";
  const inputStyle = "w-full px-4 py-3 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg text-[#2D2A26] focus:outline-none focus:border-[#C7A76C] transition-colors";
  const textareaStyle = `${inputStyle} resize-none`;

  // Max lengths for admin text fields to prevent localStorage bloat
  const MAX = { short: 200, medium: 500, long: 2000, feature: 100, faqQ: 300, faqA: 1500 };

  const sections = [
    { id: "hero" as const, label: "Hero" },
    { id: "description" as const, label: "Description" },
    { id: "showcase" as const, label: "Showcase" },
    { id: "tiers" as const, label: "Pricing Tiers" },
    { id: "certifications" as const, label: "Certifications" },
    { id: "faqs" as const, label: "FAQs" },
    { id: "inquiry" as const, label: "Inquiry Form" },
  ];

  return (
    <div className="space-y-6">
      {/* Section nav */}
      <div className="flex gap-2 flex-wrap">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`px-3 py-1.5 rounded-lg tracking-[0.08em] transition-all ${
              section === s.id
                ? "bg-[#6F7F63]/10 text-[#6F7F63] border border-[#6F7F63]/30"
                : "text-[#6F675F] border border-[#E6E0D7] hover:bg-[#F2EEE8]"
            }`}
            style={{ fontSize: "11px" }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* ─── Hero ─── */}
      {section === "hero" && (
        <div className="space-y-5 bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h3 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500 }}>
            Hero Section
          </h3>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Label</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.hero.label} onChange={(e) => update({ hero: { ...ws.hero, label: e.target.value } })} />
          </div>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Title</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.hero.title} onChange={(e) => update({ hero: { ...ws.hero, title: e.target.value } })} />
          </div>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Subtitle</label>
            <textarea rows={3} className={textareaStyle} style={{ fontSize: "14px" }} maxLength={MAX.medium} value={ws.hero.subtitle} onChange={(e) => update({ hero: { ...ws.hero, subtitle: e.target.value } })} />
          </div>
          <ImageUpload label="Hero Image" value={ws.hero.image} onChange={(url) => update({ hero: { ...ws.hero, image: url } })} previewAspect="aspect-video" />
          <TranslationsPanel
            fields={[
              { key: "label", label: "Label", englishValue: ws.hero.label },
              { key: "title", label: "Title", englishValue: ws.hero.title },
              { key: "subtitle", label: "Subtitle", type: "textarea", rows: 3, englishValue: ws.hero.subtitle },
            ]}
            i18n={ws.hero.i18n || {}}
            onChange={(i18n) => update({ hero: { ...ws.hero, i18n } })}
          />
        </div>
      )}

      {/* ─── Description ─── */}
      {section === "description" && (
        <div className="space-y-5 bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h3 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500 }}>
            Why Partner With Us
          </h3>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Label</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.description.label} onChange={(e) => update({ description: { ...ws.description, label: e.target.value } })} />
          </div>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Title</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.description.title} onChange={(e) => update({ description: { ...ws.description, title: e.target.value } })} />
          </div>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Text</label>
            <textarea rows={4} className={textareaStyle} style={{ fontSize: "14px" }} maxLength={MAX.long} value={ws.description.text} onChange={(e) => update({ description: { ...ws.description, text: e.target.value } })} />
          </div>
          <TranslationsPanel
            fields={[
              { key: "label", label: "Label", englishValue: ws.description.label },
              { key: "title", label: "Title", englishValue: ws.description.title },
              { key: "text", label: "Text", type: "textarea", rows: 4, englishValue: ws.description.text },
            ]}
            i18n={ws.description.i18n || {}}
            onChange={(i18n) => update({ description: { ...ws.description, i18n } })}
          />
        </div>
      )}

      {/* ─── Showcase ─── */}
      {section === "showcase" && (
        <div className="space-y-5 bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h3 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500 }}>
            Product Showcase
          </h3>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Label</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.showcase.label} onChange={(e) => update({ showcase: { ...ws.showcase, label: e.target.value } })} />
          </div>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Title</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.showcase.title} onChange={(e) => update({ showcase: { ...ws.showcase, title: e.target.value } })} />
          </div>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>CTA Button Text</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.showcase.cta_text} onChange={(e) => update({ showcase: { ...ws.showcase, cta_text: e.target.value } })} />
          </div>
          <ImageUpload label="Showcase Image" value={ws.showcase.image} onChange={(url) => update({ showcase: { ...ws.showcase, image: url } })} previewAspect="aspect-[4/5]" />
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Features (one per line)</label>
            <textarea
              rows={6}
              className={textareaStyle}
              style={{ fontSize: "14px" }}
              value={ws.showcase.features.join("\n")}
              onChange={(e) => update({ showcase: { ...ws.showcase, features: e.target.value.split("\n").filter((f) => f.trim()) } })}
            />
          </div>
          <TranslationsPanel
            fields={[
              { key: "label", label: "Label", englishValue: ws.showcase.label },
              { key: "title", label: "Title", englishValue: ws.showcase.title },
              { key: "cta_text", label: "CTA Button Text", englishValue: ws.showcase.cta_text },
              { key: "features", label: "Features (one per line)", type: "textarea", rows: 6, englishValue: ws.showcase.features.join("\n") },
            ]}
            i18n={ws.showcase.i18n || {}}
            onChange={(i18n) => update({ showcase: { ...ws.showcase, i18n } })}
          />
        </div>
      )}

      {/* ─── Pricing Tiers ─── */}
      {section === "tiers" && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500 }}>
              Pricing Tiers
            </h3>
            <button
              onClick={() => update({ tiers: [...ws.tiers, { ...emptyTier }] })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase"
              style={{ fontSize: "10px" }}
            >
              <Plus size={14} /> Add Tier
            </button>
          </div>
          {ws.tiers.map((tier, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[#2D2A26] tracking-[0.05em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "18px", fontWeight: 500 }}>
                  {tier.name || `Tier ${i + 1}`}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const updated = ws.tiers.map((t, idx) => ({ ...t, featured: idx === i ? !t.featured : false }));
                      update({ tiers: updated });
                    }}
                    className={`p-1.5 rounded-lg transition-colors ${tier.featured ? "text-[#C7A76C] bg-[#C7A76C]/10" : "text-[#D8D1C7] hover:text-[#C7A76C]"}`}
                    title="Set as featured"
                  >
                    <Star size={16} className={tier.featured ? "fill-[#C7A76C]" : ""} />
                  </button>
                  <button
                    onClick={() => update({ tiers: ws.tiers.filter((_, idx) => idx !== i) })}
                    className="p-1.5 text-[#D8D1C7] hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={labelStyle} style={{ fontSize: "10px" }}>Name</label>
                  <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={tier.name} onChange={(e) => {
                    const tiers = [...ws.tiers]; tiers[i] = { ...tier, name: e.target.value }; update({ tiers });
                  }} />
                </div>
                <div>
                  <label className={labelStyle} style={{ fontSize: "10px" }}>Min Order</label>
                  <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={tier.minOrder} onChange={(e) => {
                    const tiers = [...ws.tiers]; tiers[i] = { ...tier, minOrder: e.target.value }; update({ tiers });
                  }} />
                </div>
                <div>
                  <label className={labelStyle} style={{ fontSize: "10px" }}>Price Range</label>
                  <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={tier.priceRange} onChange={(e) => {
                    const tiers = [...ws.tiers]; tiers[i] = { ...tier, priceRange: e.target.value }; update({ tiers });
                  }} />
                </div>
              </div>
              <div>
                <label className={labelStyle} style={{ fontSize: "10px" }}>Features (one per line)</label>
                <textarea
                  rows={4}
                  className={textareaStyle}
                  style={{ fontSize: "14px" }}
                  value={tier.features.join("\n")}
                  onChange={(e) => {
                    const tiers = [...ws.tiers]; tiers[i] = { ...tier, features: e.target.value.split("\n").filter((f) => f.trim()) }; update({ tiers });
                  }}
                />
              </div>
              <TranslationsPanel
                fields={[
                  { key: "name", label: "Tier Name", englishValue: tier.name },
                  { key: "minOrder", label: "Min Order", englishValue: tier.minOrder },
                  { key: "priceRange", label: "Price Range", englishValue: tier.priceRange },
                  { key: "features", label: "Features (one per line)", type: "textarea", rows: 4, englishValue: tier.features.join("\n") },
                ]}
                i18n={tier.i18n || {}}
                onChange={(i18n) => {
                  const tiers = [...ws.tiers]; tiers[i] = { ...tier, i18n }; update({ tiers });
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* ─── Certifications ─── */}
      {section === "certifications" && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500 }}>
              Certifications
            </h3>
            <button
              onClick={() => update({ certifications: [...ws.certifications, { title: "", desc: "" }] })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase"
              style={{ fontSize: "10px" }}
            >
              <Plus size={14} /> Add
            </button>
          </div>
          {ws.certifications.map((cert, i) => (
            <div key={i} className="bg-white rounded-xl p-5 shadow-[0_2px_16px_rgba(40,32,20,0.04)] space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-1 space-y-3">
                  <div>
                    <label className={labelStyle} style={{ fontSize: "10px" }}>Title</label>
                    <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={cert.title} onChange={(e) => {
                      const certs = [...ws.certifications]; certs[i] = { ...cert, title: e.target.value }; update({ certifications: certs });
                    }} />
                  </div>
                  <div>
                    <label className={labelStyle} style={{ fontSize: "10px" }}>Description</label>
                    <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.medium} value={cert.desc} onChange={(e) => {
                      const certs = [...ws.certifications]; certs[i] = { ...cert, desc: e.target.value }; update({ certifications: certs });
                    }} />
                  </div>
                </div>
                <button
                  onClick={() => update({ certifications: ws.certifications.filter((_, idx) => idx !== i) })}
                  className="p-1.5 text-[#D8D1C7] hover:text-red-400 transition-colors mt-6"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <TranslationsPanel
                fields={[
                  { key: "title", label: "Title", englishValue: cert.title },
                  { key: "desc", label: "Description", englishValue: cert.desc },
                ]}
                i18n={cert.i18n || {}}
                onChange={(i18n) => {
                  const certs = [...ws.certifications]; certs[i] = { ...cert, i18n }; update({ certifications: certs });
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* ─── FAQs ─── */}
      {section === "faqs" && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500 }}>
              Wholesale FAQs
            </h3>
            <button
              onClick={() => update({ faqs: [...ws.faqs, { ...emptyFaq }] })}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6F7F63] text-white rounded-lg hover:bg-[#5F6F53] transition-colors tracking-[0.1em] uppercase"
              style={{ fontSize: "10px" }}
            >
              <Plus size={14} /> Add FAQ
            </button>
          </div>
          {ws.faqs.map((faq, i) => (
            <div key={i} className="bg-white rounded-xl p-5 shadow-[0_2px_16px_rgba(40,32,20,0.04)] space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div>
                    <label className={labelStyle} style={{ fontSize: "10px" }}>Question</label>
                    <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.faqQ} value={faq.q} onChange={(e) => {
                      const faqs = [...ws.faqs]; faqs[i] = { ...faq, q: e.target.value }; update({ faqs });
                    }} />
                  </div>
                  <div>
                    <label className={labelStyle} style={{ fontSize: "10px" }}>Answer</label>
                    <textarea rows={3} className={textareaStyle} style={{ fontSize: "14px" }} maxLength={MAX.faqA} value={faq.a} onChange={(e) => {
                      const faqs = [...ws.faqs]; faqs[i] = { ...faq, a: e.target.value }; update({ faqs });
                    }} />
                  </div>
                </div>
                <div className="flex flex-col gap-1 ml-3">
                  {i > 0 && (
                    <button onClick={() => { const faqs = [...ws.faqs]; [faqs[i - 1], faqs[i]] = [faqs[i], faqs[i - 1]]; update({ faqs }); }} className="p-1 text-[#D8D1C7] hover:text-[#6F7F63]"><ChevronUp size={16} /></button>
                  )}
                  {i < ws.faqs.length - 1 && (
                    <button onClick={() => { const faqs = [...ws.faqs]; [faqs[i], faqs[i + 1]] = [faqs[i + 1], faqs[i]]; update({ faqs }); }} className="p-1 text-[#D8D1C7] hover:text-[#6F7F63]"><ChevronDown size={16} /></button>
                  )}
                  <button onClick={() => update({ faqs: ws.faqs.filter((_, idx) => idx !== i) })} className="p-1 text-[#D8D1C7] hover:text-red-400"><Trash2 size={16} /></button>
                </div>
              </div>
              <TranslationsPanel
                fields={[
                  { key: "q", label: "Question", englishValue: faq.q },
                  { key: "a", label: "Answer", type: "textarea", rows: 3, englishValue: faq.a },
                ]}
                i18n={faq.i18n || {}}
                onChange={(i18n) => {
                  const faqs = [...ws.faqs]; faqs[i] = { ...faq, i18n }; update({ faqs });
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* ─── Inquiry Form ─── */}
      {section === "inquiry" && (
        <div className="space-y-5 bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <h3 className="text-[#2D2A26] tracking-[0.08em]" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "20px", fontWeight: 500 }}>
            Inquiry Form Section
          </h3>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Label</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.inquiry.label} onChange={(e) => update({ inquiry: { ...ws.inquiry, label: e.target.value } })} />
          </div>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Title</label>
            <input className={inputStyle} style={{ fontSize: "14px" }} maxLength={MAX.short} value={ws.inquiry.title} onChange={(e) => update({ inquiry: { ...ws.inquiry, title: e.target.value } })} />
          </div>
          <div>
            <label className={labelStyle} style={{ fontSize: "10px" }}>Subtitle</label>
            <textarea rows={2} className={textareaStyle} style={{ fontSize: "14px" }} maxLength={MAX.medium} value={ws.inquiry.subtitle} onChange={(e) => update({ inquiry: { ...ws.inquiry, subtitle: e.target.value } })} />
          </div>
          <TranslationsPanel
            fields={[
              { key: "label", label: "Label", englishValue: ws.inquiry.label },
              { key: "title", label: "Title", englishValue: ws.inquiry.title },
              { key: "subtitle", label: "Subtitle", type: "textarea", rows: 2, englishValue: ws.inquiry.subtitle },
            ]}
            i18n={ws.inquiry.i18n || {}}
            onChange={(i18n) => update({ inquiry: { ...ws.inquiry, i18n } })}
          />
        </div>
      )}
    </div>
  );
}
